/**
 * PATH: packages/email-hub/src/feature/lib/resurfaceTimes.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-RESURFACETIMES-001
 * PURPOSE: Pure preset builders for the resurface train — snooze wake
 *          times, send-later fire times, and follow-up nudge deadlines —
 *          plus the shared label formatter.
 * WHY: Snooze (row context menu), Send later (composer split button) and
 *      "remind me if no reply" (composer) all need "human moment → Date"
 *      presets. One pure module keeps the three surfaces consistent and
 *      unit-testable (DST/midnight edges live HERE, not in JSX).
 * FLOW:
 *   ┌──────────┐   ┌──────────────────────┐   ┌─────────────────────┐
 *   │ now:Date │ → │ preset arithmetic     │ → │ {key,label,at:Date}[]│
 *   └──────────┘   └──────────────────────┘   └─────────────────────┘
 * RELATED:
 *   - EmailContextMenu.tsx: Snooze submenu consumes snoozePresets
 *   - ComposePage.tsx: Send-later menu consumes sendLaterPresets
 *   - organisation/tasks/email_resurface_tasks.py: the beat that wakes
 */

export interface ResurfacePreset {
  key: string
  label: string
  at: Date
}

const MORNING_HOUR = 8
const EVENING_HOUR = 18

function at(base: Date, dayOffset: number, hour: number): Date {
  const d = new Date(base)
  d.setDate(d.getDate() + dayOffset)
  d.setHours(hour, 0, 0, 0)
  return d
}

/** Days until next Monday, always in the FUTURE (today=Mon → 7). */
function daysToNextMonday(base: Date): number {
  const dow = base.getDay() // 0=Sun … 6=Sat
  const delta = (8 - dow) % 7
  return delta === 0 ? 7 : delta
}

/** Snooze presets — when a hidden inbox row resurfaces (unread). */
export function snoozePresets(now: Date = new Date()): ResurfacePreset[] {
  const inThreeHours = new Date(now.getTime() + 3 * 60 * 60 * 1000)
  return [
    { key: 'later_today', label: 'Later today (3 hours)', at: inThreeHours },
    {
      key: 'tomorrow',
      label: 'Tomorrow morning (8 AM)',
      at: at(now, 1, MORNING_HOUR),
    },
    {
      key: 'next_week',
      label: `Next week (Mon 8 AM)`,
      at: at(now, daysToNextMonday(now), MORNING_HOUR),
    },
  ]
}

/** Send-later presets — when a scheduled draft fires. */
export function sendLaterPresets(now: Date = new Date()): ResurfacePreset[] {
  const presets: ResurfacePreset[] = []
  const eveningToday = at(now, 0, EVENING_HOUR)
  if (eveningToday.getTime() > now.getTime() + 5 * 60 * 1000) {
    presets.push({
      key: 'this_evening', label: 'This evening (6 PM)', at: eveningToday,
    })
  }
  presets.push(
    {
      key: 'tomorrow_morning',
      label: 'Tomorrow morning (8 AM)',
      at: at(now, 1, MORNING_HOUR),
    },
    {
      key: 'monday_morning',
      label: 'Monday morning (8 AM)',
      at: at(now, daysToNextMonday(now), MORNING_HOUR),
    },
  )
  return presets
}

/** Follow-up nudge presets — "remind me if no reply by …". */
export function followupPresets(now: Date = new Date()): ResurfacePreset[] {
  return [
    { key: 'in_3_days', label: 'in 3 days', at: at(now, 3, MORNING_HOUR) },
    { key: 'in_1_week', label: 'in 1 week', at: at(now, 7, MORNING_HOUR) },
  ]
}

/** Compact human label for a wake/fire time, e.g. "Tue 8:00 AM" (this
 *  week) or "21 Jul, 8:00 AM" (further out). Pure; locale-default. */
export function formatResurfaceTime(
  iso: string | Date,
  now: Date = new Date(),
): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric', minute: '2-digit',
  })
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return time
  const withinWeek = d.getTime() - now.getTime() < 6 * 24 * 60 * 60 * 1000
  if (withinWeek && d.getTime() > now.getTime()) {
    return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`
  }
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${time}`
}
